import { NavLink, useLocation } from 'react-router-dom';
import {
  CaretDoubleLeft,
  Gear,
  SignOut,
  Newspaper,
  BookOpen,
  Compass,
  Books,
  Database,
  Lightbulb,
} from '@phosphor-icons/react';
import { twMerge } from 'tailwind-merge';
import logo from '@/assets/logo.png';
import { useAuth } from '@/lib/auth';
import { useGetUserDetails } from '@/features/settings/api/getUserDetails';
import { useFeatureFlagEnabled } from 'posthog-js/react';

const NavigationStyles =
  'border-none rounded-xl shadow-none group flex justify-start items-center px-2 py-2';

export const Sidebar = ({ isCollapsed, toggleSidebar }) => {
  const location = useLocation();
  const { logout, user } = useAuth();

  const storedEmail = localStorage.getItem('email');

  const { data } = useGetUserDetails();

  const isDeepDivesEnabled = useFeatureFlagEnabled('deep-dives');
  const isPetaReportsEnabled = useFeatureFlagEnabled('peta-reports');
  const isTemplatesEnabled = useFeatureFlagEnabled('templates');
  const isSettingsIntegrationsEnabled = useFeatureFlagEnabled(
    'settings-integrations'
  );

  function getInitials(fullName) {
    // Function to extract initials from a string
    const extractInitials = (str) =>
      str
        .split(/[_.]/)
        .map((part) => part[0])
        .join('')
        .toUpperCase();

    if (fullName) {
      const names = fullName?.split(' ');
      const initials = names
        .map((name, index) =>
          index === 0 || index === names?.length - 1 ? name[0] : ''
        )
        .filter(Boolean);

      // If no initials are found from the name, use the email initials
      if (initials.length === 0 && storedEmail) {
        const emailInitials = extractInitials(storedEmail?.split('@')[0]);
        return emailInitials;
      }

      return initials.join('').toUpperCase();
    } else if (storedEmail) {
      // If name is not available, use the email initials
      const emailInitials = extractInitials(storedEmail?.split('@')[0]);
      return emailInitials;
    }
    return '';
  }

  const handleRemoveData = () => {
    localStorage.removeItem('email');
  };

  let navigation = [
    {
      name: 'Explore',
      to: `explore`,
      icon: Compass,
      show: true,
    },
    {
      name: 'Deep Dives',
      to: `deep-dives`,
      icon: Lightbulb,
      role: 'admin',
      show: isDeepDivesEnabled,
    },
    {
      name: 'PetaReports',
      to: `peta-reports`,
      icon: Newspaper,
      role: 'admin',
      show: isPetaReportsEnabled,
    },
    {
      name: 'Templates',
      to: `templates`,
      icon: BookOpen,
      role: 'admin',
      show: isTemplatesEnabled,
    },
    {
      name: 'Data Catalog',
      to: `data-catalog`,
      icon: Database,
      show: true,
    },
    {
      name: 'Settings',
      to: isSettingsIntegrationsEnabled
        ? `settings/integrations`
        : 'settings/user-management',
      icon: Gear,
      role: 'admin',
      show: true,
    },
  ];

  if (user) {
    navigation = navigation.filter((item) => {
      if (!item.role) {
        return true;
      }
      return user.role?.toLowerCase() === item.role;
    });
  }

  const extras = [
    {
      name: 'Logout',
      icon: SignOut,
      onClick: () => {
        logout();
        handleRemoveData();
      },
    },
  ];
  const isLinkActive = (linkText) => {
    return location.pathname.includes(`admin/${linkText}/`);
  };
  return (
    <section
      className={`border-r border-outline-light flex flex-col justify-between h-full bg-white ${
        isCollapsed ? 'w-12' : 'w-64'
      } duration-300 transition-width h-full`}
    >
      <div className={`flex flex-col ${isCollapsed ? 'p-1' : 'p-4'} h-full`}>
        {isCollapsed ? (
          // <PetavueIcon />
          <img src={logo} alt="" width={35} className="mx-auto" />
        ) : (
          <div className="flex justify-between items-center">
            <span className="flex flex-row gap-4 items-center">
              {/* <PetavueIcon /> */}
              <img src={logo} alt="" width={40} />
              <h2 className="leading-7 font-bold">Petavue</h2>
            </span>
            <button
              className={`text-brand-dark-gray-400 ${
                isCollapsed && 'border-t-2 border-b-2'
              }`}
              onClick={toggleSidebar}
            >
              <CaretDoubleLeft size={24} />
            </button>
          </div>
        )}
        {/* {isCollapsed && (
          <div className="border-t-2 border-b-2 flex justify-center my-3 py-4">
            <button
              className={`text-brand-dark-gray-400 `}
              onClick={toggleSidebar}
            >
              <CaretDoubleRight size={20} />
            </button>
          </div>
        )} */}

        <div
          className={`border-t my-3 pt-4 flex flex-col ${
            isCollapsed ? ' items-center' : 'mt-7'
          } duration-300 justify-between flex-grow`}
        >
          <div className="flex flex-col gap-4 pb-6">
            {navigation?.map(
              (item, index) =>
                item.show && (
                  <NavLink
                    end={index === 0}
                    key={item.name}
                    to={item.to}
                    className={({ isActive }) =>
                      isActive ||
                      isLinkActive(item.to) ||
                      location.pathname.includes(
                        `admin/${item.name.toLowerCase()}/`
                      )
                        ? `${NavigationStyles} text-brand-ai-100 bg-brand-ai-gray-500 w-full`
                        : `${NavigationStyles} bg-transparent text-brand-dark-gray-400 hover:text-brand-ai-100 hover:bg-brand-ai-gray-500 w-full`
                    }
                  >
                    <div className="relative">
                      <div className="flex gap-4 items-center">
                        <item.icon
                          className={twMerge(
                            `group-hover:text-brand-ai-100`,
                            location.pathname.includes(`/admin/${item.to}`) ||
                              location.pathname.includes(
                                `admin/${item.name.toLowerCase()}/`
                              )
                              ? 'text-brand-ai-100'
                              : 'text-gray-400',
                            `flex-shrink-0 h-5 w-5 text-sm`
                          )}
                          aria-hidden="true"
                          weight={
                            location.pathname.includes(`/admin/${item.to}`) ||
                            location.pathname.includes(
                              `admin/${item.name.toLowerCase()}/`
                            )
                              ? 'fill'
                              : 'regular'
                          }
                          fontSize={24}
                        />
                        {!isCollapsed && item.name}
                      </div>
                      <div className="absolute -top-1 z-10 left-full ml-3.5 px-3 py-1 bg-white leading-5 font-normal text-brand-ai-200 text-xs hidden group-hover:block rounded-lg border border-solid border-brand-ai-700 card-shadow whitespace-nowrap">
                        {item.name}
                      </div>
                    </div>
                  </NavLink>
                )
            )}
          </div>
          <div className="flex flex-col gap-4">
            {extras?.map((item, index) => (
              <button
                key={item.name}
                className={`${NavigationStyles} hover:bg-brand-ai-700 text-brand-ai-100 w-full`}
                onClick={item?.onClick}
              >
                <div className="relative">
                  <div className="flex gap-4 items-center">
                    <item.icon
                      className={twMerge(
                        `group-hover:text-brand-ai-200 text-brand-ai-gray-100`,
                        `flex-shrink-0 h-5 w-5 text-sm`
                      )}
                      aria-hidden="true"
                      fontSize={24}
                    />
                    {!isCollapsed && item.name}
                  </div>
                  <div className="absolute -top-1 z-10 left-full ml-3.5 px-3 py-1 bg-white leading-5 font-normal text-brand-ai-200 text-xs hidden group-hover:block rounded-lg border border-solid border-brand-ai-700 card-shadow whitespace-nowrap">
                    {item.name}
                  </div>
                </div>
              </button>
            ))}
          </div>
        </div>
      </div>
      <div className={`${isCollapsed && 'block'}`}>
        <div className="flex gap-4 pb-4 items-center justify-center">
          <div className="bg-pv-primary-primary-100 flex items-center justify-center mx-auto rounded-full w-9 h-9 overflow-hidden">
            <NavLink
              end
              key="User Profile"
              to="/admin/my-profile"
              style={{ textDecoration: 'none' }}
              className={({ isActive }) =>
                `${
                  isActive ||
                  location.pathname.includes(`admin/my-profile/`) ||
                  location.pathname === '/admin/my-profile'
                    ? 'border-pv-primary-primary-500 border border-solid'
                    : ''
                } flex items-center justify-center text-brand-ai-200 font-medium text-xs w-full h-full rounded-full hover:text-brand-ai-200`
              }
            >
              {getInitials(data?.name)}
            </NavLink>
          </div>
          {!isCollapsed && (
            <span className="flex flex-col gap-1">
              <h2>Prasanna V.</h2>
              <p className="text-xs">pv@petavue.com</p>
            </span>
          )}
        </div>
      </div>
    </section>
  );
};
